/*
  Souq Al Oud - mobile sticky purchase bar (snippets/souq-sticky-atc.liquid)

  The bar's button is associated with the main product form through the HTML `form`
  attribute, so product-form.js handles the add to cart exactly as it does for the main
  button. This element only decides when the bar is visible and mirrors the main button
  and price, which Dawn updates in place on every variant change.

  Because it is a custom element, it re-initialises by itself when Dawn swaps the whole
  product-info block (combined listings) or when the Theme Editor re-renders the section.
*/
if (!customElements.get('souq-sticky-atc')) {
  customElements.define(
    'souq-sticky-atc',
    class SouqStickyAtc extends HTMLElement {
      connectedCallback() {
        const sectionId = this.dataset.section;
        this.mainButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
        this.mainPrice = document.getElementById(`price-${sectionId}`);
        this.button = this.querySelector('[data-souq-sticky-button]');
        this.priceSlot = this.querySelector('[data-souq-sticky-price]');
        if (!this.mainButton || !this.button) return;

        this.spinner = this.button.querySelector('.loading__spinner');
        this.label = this.button.querySelector('span');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.footer = document.querySelector('footer');
        this.pastButton = false;
        this.footerVisible = false;
        this.frame = 0;
        this.measureFrame = 0;
        this.onQueryChange = () => this.scheduleMeasure();
        this.onScroll = () => this.scheduleMeasure();

        this.syncState();
        this.observeState();
        this.measure();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.mobileQuery.addEventListener('change', this.onQueryChange);

        // Only now may CSS show the bar: without JavaScript it stays absent.
        this.classList.add('is-ready');
      }

      disconnectedCallback() {
        this.mutationObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.mobileQuery?.removeEventListener('change', this.onQueryChange);
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.measureFrame) cancelAnimationFrame(this.measureFrame);
      }

      observeState() {
        this.mutationObserver = new MutationObserver(() => this.scheduleSync());
        this.mutationObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          characterData: true,
          subtree: true,
        });
        if (this.mainPrice) {
          this.mutationObserver.observe(this.mainPrice, {
            attributes: true,
            attributeFilter: ['class'],
            childList: true,
            characterData: true,
            subtree: true,
          });
        }
      }

      scheduleMeasure() {
        if (this.measureFrame) return;
        this.measureFrame = requestAnimationFrame(() => {
          this.measureFrame = 0;
          this.measure();
        });
      }

      // Visible only once the main button has scrolled out of view above the viewport,
      // and not while the footer is on screen, so the bar never covers the end of the page.
      measure() {
        const rect = this.mainButton.getBoundingClientRect();
        this.pastButton = rect.height > 0 && rect.bottom <= 0;
        this.footerVisible = this.footer ? this.footer.getBoundingClientRect().top < window.innerHeight : false;
        this.update();
      }

      update() {
        const show = this.mobileQuery.matches && this.pastButton && !this.footerVisible;
        this.classList.toggle('is-visible', show);
      }

      scheduleSync() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.syncState();
        });
      }

      syncState() {
        const main = this.mainButton;
        const loading = main.classList.contains('loading');

        this.button.toggleAttribute('disabled', main.hasAttribute('disabled'));
        const ariaDisabled = main.getAttribute('aria-disabled');
        if (ariaDisabled === null) {
          this.button.removeAttribute('aria-disabled');
        } else {
          this.button.setAttribute('aria-disabled', ariaDisabled);
        }
        this.button.classList.toggle('loading', loading);
        this.spinner?.classList.toggle('hidden', !loading);

        const mainLabel = main.querySelector('span');
        if (this.label && mainLabel) {
          const text = mainLabel.textContent.trim();
          if (this.label.textContent !== text) this.label.textContent = text;
        }

        if (this.priceSlot && this.mainPrice) {
          const markup = this.mainPrice.innerHTML;
          if (this.priceMarkup !== markup) {
            this.priceMarkup = markup;
            this.priceSlot.innerHTML = markup;
          }
          this.priceSlot.hidden = this.mainPrice.classList.contains('hidden');
        }
      }
    }
  );
}
